import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, FileText, Info, Loader2, Upload } from "lucide-react";

import { gradivostChat, recognizeDocument } from "@/api/client";
import type { GradivostChatResponse } from "@/api/schemas";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  extractZkFacts,
  ownersSummary,
  parcelDocChecks,
  zkChatText,
  type ParcelDocCheck,
} from "@/features/feasibility/parcelDoc";
import { useGradivostChatStore } from "@/features/feasibility/useGradivostChatStore";

/**
 * G1 — koš „dokumenti čestice" u modulu gradivosti.
 *
 * Drop ZK/katastarskog izvatka → postojeći `recognize-document`
 * (ekstrakcija radi savršeno na stvarnom ZK-u, vidi G1_PLAN_IZVEDBE) →
 * činjenice čestice → postojeći `gradivostChat` (parse + G0 auto-zona),
 * pa se forma hidratira ISTIM putem kao chat/klik na zonu. Nula novih
 * backend ruta.
 */
export function ParcelDocDrop({
  onAnswer,
}: {
  /** Isti hidratacijski put kao chat — roditelj puni formu i pokreće provjeru. */
  onAnswer: (resp: GradivostChatResponse) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const addTurn = useGradivostChatStore((s) => s.addTurn);
  const setParcelDoc = useGradivostChatStore((s) => s.setParcelDoc);
  const parcelDoc = useGradivostChatStore((s) => s.parcelDoc);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const recognized = await recognizeDocument(file, "katastar_vlasnicki");
      const facts = extractZkFacts(recognized);
      if (!facts) {
        throw new Error(
          "Ovo ne izgleda kao zemljišnoknjižni / katastarski izvadak. Učitaj ZK izvadak čestice.",
        );
      }
      const text = zkChatText(facts);
      if (!text) {
        throw new Error("U izvatku nisam pronašao broj čestice — provjeri dokument.");
      }
      const resp = await gradivostChat(text, null);
      return { facts, text, resp };
    },
    onSuccess: ({ facts, text, resp }) => {
      // Cross-check protiv G0 (površina iz auto-zone) — mandat: neslaganje = nalaz.
      const g0Area = resp.zone?.parcel?.area_m2 ?? null;
      const checks = parcelDocChecks(facts, g0Area);
      setParcelDoc({ facts, checks });
      addTurn(`Iz ZK izvatka: ${text}`, resp);
      onAnswer(resp);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Greška pri čitanju dokumenta."),
  });

  const onDrop = useCallback(
    (accepted: File[]) => {
      const pdf = accepted.find((f) => f.name.toLowerCase().endsWith(".pdf"));
      if (!pdf) {
        setError("Učitaj ZK izvadak u PDF formatu.");
        return;
      }
      setError(null);
      upload.mutate(pdf);
    },
    [upload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { "application/pdf": [".pdf"] },
  });

  const facts = parcelDoc?.facts;
  const owners = facts ? ownersSummary(facts.vlasnici) : null;

  return (
    <div className="mb-5">
      <div
        {...getRootProps()}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed bg-white px-4 py-3 transition-all",
          isDragActive ? "border-ind-500 bg-ind-50" : "border-stone-300 hover:border-ind-400 hover:bg-stone-50",
          upload.isPending && "pointer-events-none opacity-70",
        )}
        data-testid="parcel-doc-drop"
      >
        <input {...getInputProps()} />
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-ind-100 text-ind-700">
          {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[0.88rem] font-semibold text-stone-800">
            {upload.isPending ? "Čitam izvadak…" : "Imaš ZK izvadak? Ubaci ga"}
          </p>
          <p className="text-[0.76rem] text-stone-500">
            Očitam česticu, k.o., površinu i terete iz izvatka i popunim provjeru.
          </p>
        </div>
        {!upload.isPending && (
          <Button variant="outline" size="sm" type="button" className="flex-shrink-0">
            <Upload className="h-3.5 w-3.5" />
            Odaberi
          </Button>
        )}
      </div>

      {error && (
        <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[0.8rem] text-red-700">
          {error}
        </p>
      )}

      {facts && (
        <div
          className="mt-3 rounded-lg border border-stone-200 bg-stone-50 p-3"
          data-testid="parcel-doc-card"
        >
          <p className="mb-1.5 text-[0.75rem] font-semibold uppercase tracking-[0.04em] text-stone-500">
            Iz dokumenta čestice
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[0.83rem] text-stone-700">
            {facts.kc && <span>čestica <strong>{facts.kc}</strong></span>}
            {facts.ko && <span>k.o. <strong>{facts.ko}</strong></span>}
            {facts.povrsinaM2 != null && (
              <span>površina <strong>{facts.povrsinaM2.toLocaleString("hr-HR")} m²</strong></span>
            )}
            {facts.nacinUporabe && <span>uporaba: {facts.nacinUporabe}</span>}
            {owners && <span>vlasnik: {owners}</span>}
          </div>
          {parcelDoc.checks.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="parcel-doc-checks">
              {parcelDoc.checks.map((c, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[0.8rem]">
                  <CheckIcon kind={c.kind} />
                  <span
                    className={cn(
                      c.kind === "warn" && "text-amber-800",
                      c.kind === "ok" && "text-green-800",
                      c.kind === "info" && "text-stone-600",
                    )}
                  >
                    {c.text}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[0.72rem] text-stone-400">
            Podaci iz dokumenta — provjeri prije oslanjanja.
          </p>
        </div>
      )}
    </div>
  );
}

function CheckIcon({ kind }: { kind: ParcelDocCheck["kind"] }) {
  if (kind === "warn") return <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />;
  if (kind === "ok") return <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-600" />;
  return <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-stone-400" />;
}
